import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { KeyRound, User, Save, ShieldAlert } from "lucide-react";

export default function AdminSettings() {
  const { user, refresh } = useAuth();
  const [profile, setProfile] = useState({ name: "", email: "" });
  const [passwords, setPasswords] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [changingPw, setChangingPw] = useState(false);

  useEffect(() => {
    if (user) setProfile({ name: user.name || "", email: user.email || "" });
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.patch("/auth/profile", profile);
      await refresh();
      toast.success("Profil berhasil diperbarui");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (passwords.new_password !== passwords.confirm) {
      toast.error("Konfirmasi password baru tidak cocok");
      return;
    }
    if (passwords.new_password.length < 8) {
      toast.error("Password baru minimal 8 karakter");
      return;
    }
    setChangingPw(true);
    try {
      await api.post("/auth/change-password", {
        current_password: passwords.current_password,
        new_password: passwords.new_password,
      });
      toast.success("Password berhasil diubah");
      setPasswords({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setChangingPw(false);
    }
  };

  return (
    <div data-testid="admin-settings">
      <h1 className="text-3xl font-bold text-slate-900">Pengaturan Akun</h1>
      <p className="text-slate-600 mt-1">Kelola profil dan keamanan akun admin Anda.</p>

      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        {/* Profile */}
        <form onSubmit={saveProfile} className="card-sf p-6" data-testid="admin-profile-form">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-xl bg-[var(--brand-blue)] text-white flex items-center justify-center"><User className="h-5 w-5" /></div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Profil</h2>
              <p className="text-xs text-slate-500">Nama & email login admin.</p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Nama</label>
              <input
                type="text"
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                data-testid="profile-name"
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[var(--brand-blue)]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Email</label>
              <input
                type="email"
                value={profile.email}
                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                data-testid="profile-email"
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[var(--brand-blue)]"
              />
            </div>
            <button type="submit" disabled={savingProfile} data-testid="profile-save" className="btn-secondary">
              <Save className="h-4 w-4" /> {savingProfile ? "Menyimpan..." : "Simpan Profil"}
            </button>
          </div>
        </form>

        {/* Password */}
        <form onSubmit={changePassword} className="card-sf p-6" data-testid="admin-password-form">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-xl bg-[var(--brand-orange)] text-white flex items-center justify-center"><KeyRound className="h-5 w-5" /></div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Ubah Password</h2>
              <p className="text-xs text-slate-500">Minimal 8 karakter.</p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Password Saat Ini</label>
              <input
                type="password"
                required
                value={passwords.current_password}
                onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })}
                data-testid="pw-current"
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[var(--brand-blue)]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Password Baru</label>
              <input
                type="password"
                required
                minLength={8}
                value={passwords.new_password}
                onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })}
                data-testid="pw-new"
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[var(--brand-blue)]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Konfirmasi Password Baru</label>
              <input
                type="password"
                required
                minLength={8}
                value={passwords.confirm}
                onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                data-testid="pw-confirm"
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[var(--brand-blue)]"
              />
            </div>
            <button type="submit" disabled={changingPw} data-testid="pw-submit" className="btn-primary">
              <KeyRound className="h-4 w-4" /> {changingPw ? "Memproses..." : "Ubah Password"}
            </button>
          </div>
        </form>
      </div>

      <div className="mt-6 rounded-2xl bg-amber-50 border border-amber-200 p-5 flex items-start gap-3">
        <ShieldAlert className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          <div className="font-semibold mb-1">Tip Keamanan Produksi</div>
          <ul className="list-disc list-inside space-y-1 text-amber-800/90">
            <li>Segera ganti password default setelah deploy pertama kali.</li>
            <li>Gunakan password minimum 12 karakter dengan campuran huruf, angka, dan simbol.</li>
            <li>Untuk reset darurat, jalankan di server: <code className="bg-white px-1.5 py-0.5 rounded text-xs">cd backend &amp;&amp; python reset_admin.py --email {profile.email || "admin@..."} --password YourNewPass</code></li>
          </ul>
        </div>
      </div>
    </div>
  );
}
